import { cn } from '@/utils/styles';
import React, { forwardRef } from 'react';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: string;
  defaultValue?: string;
  hasError?: boolean;
  hasValue?: boolean;
  isDirty?: boolean;
  isFieldRequired?: boolean;
  success?: boolean;
  rightIcon?: React.ReactNode;
  wrapperClassName?: string;
  innerWrapperClassName?: string;
}

const Input = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  // Destructure custom props so they are not spread to <input>
  const {
    className = '',
    success = false,
    error,
    defaultValue,
    rightIcon,
    wrapperClassName = '',
    innerWrapperClassName = '',
    ...inputProps
  } = props;

  let inputClasses = `h-11 w-full rounded-lg border appearance-none px-4 py-2.5 text-sm shadow-theme-xs placeholder:text-gray-400 focus:outline-hidden focus:ring-3 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800`;

  if (inputProps.disabled) {
    inputClasses += ` text-gray-500 border-gray-300 cursor-not-allowed dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700`;
  } else if (error) {
    inputClasses += ` text-error-800 border-error-500 focus:ring-3 focus:ring-error-500/10  dark:text-error-400 dark:border-error-500`;
  } else if (success) {
    inputClasses += ` text-success-500 border-success-400 focus:ring-success-500/10 focus:border-success-300  dark:text-success-400 dark:border-success-500`;
  } else {
    inputClasses += ` bg-transparent text-gray-800 border-gray-300 focus:border-[color:var(--color-brand-primary)] focus:ring-1 focus:ring-[color:var(--color-brand-primary)]/10 hover:border-[color:var(--color-brand-primary)] dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-[color:var(--color-brand-primary)] dark:focus:ring-[color:var(--color-brand-primary)]/10`;
  }

  return (
    <div className={cn(wrapperClassName)}>
      <div className={cn('relative', innerWrapperClassName)}>
        <input ref={ref} className={cn(inputClasses, className)} defaultValue={defaultValue} {...inputProps} />
        {rightIcon && (
          <span className="absolute top-1/2 right-4 z-30 -translate-y-1/2 cursor-pointer">
            {rightIcon}
          </span>
        )}
      </div>
      {error && <p className="text-error-500 mt-1.5 text-xs">{error}</p>}
    </div>
  );
});

Input.displayName = 'Input';

export default Input;
